/**
 * Engagement HUD — Phaser canvas wrapper + game scene.
 *
 * Night ops aviation-themed math fact fluency game.
 * React creates the container; Phaser renders the gameplay.
 * Communication: Phaser → React via custom DOM events.
 */

import { useEffect, useRef, useCallback } from 'react'
import Phaser from 'phaser'
import { Howl } from 'howler'
import type { Contact, ContactResult, FactStrikeAnswerEvent, FactStrikeCompleteEvent } from './types'

// ─── Colors ───────────────────────────────────────────────────────
const HUD_GREEN = 0x00ff41
const HUD_GREEN_CSS = '#00FF41'
const HUD_GREEN_DIM = '#00FF4166'
const HUD_BG = '#020617'

// ─── SFX ──────────────────────────────────────────────────────────
const sfx = {
  lock: null as Howl | null,
  neg: null as Howl | null,
  streak: null as Howl | null,
  click: null as Howl | null,
  mission: null as Howl | null,
}

function loadSFX() {
  const base = '/audio/fact-strike'
  sfx.lock = new Howl({ src: [`${base}/lock.wav`], volume: 0.5 })
  sfx.neg = new Howl({ src: [`${base}/neg.wav`], volume: 0.4 })
  sfx.streak = new Howl({ src: [`${base}/streak.wav`], volume: 0.4 })
  sfx.click = new Howl({ src: [`${base}/click.wav`], volume: 0.3 })
  sfx.mission = new Howl({ src: [`${base}/mission.wav`], volume: 0.5 })
}

// ─── Phaser Scene ─────────────────────────────────────────────────

class FactStrikeScene extends Phaser.Scene {
  private contacts: Contact[] = []
  private currentIndex = 0
  private contactStartTime = 0
  private results: ContactResult[] = []
  private streak = 0
  private bestStreak = 0
  private score = 0
  private attempts = 0  // attempts for current contact

  // Display objects
  private problemText!: Phaser.GameObjects.Text
  private answerButtons: Phaser.GameObjects.Container[] = []
  private engagementText!: Phaser.GameObjects.Text
  private streakText!: Phaser.GameObjects.Text
  private scoreText!: Phaser.GameObjects.Text
  private reticleGraphics!: Phaser.GameObjects.Graphics
  private scanlineGraphics!: Phaser.GameObjects.Graphics
  private targetLockLabel!: Phaser.GameObjects.Text

  // Answer button positions (relative to center, arranged in diamond)
  private readonly buttonPositions = [
    { x: 0, y: -120 },   // top
    { x: 160, y: 0 },    // right
    { x: 0, y: 120 },    // bottom
    { x: -160, y: 0 },   // left
  ]

  constructor() {
    super({ key: 'FactStrikeScene' })
  }

  create() {
    const { width, height } = this.scale
    const cx = width / 2
    const cy = height / 2

    // Background gradient
    const bg = this.add.graphics()
    bg.fillGradientStyle(0x020617, 0x020617, 0x0f172a, 0x0f172a, 1)
    bg.fillRect(0, 0, width, height)

    // Scanlines overlay
    this.scanlineGraphics = this.add.graphics()
    this.scanlineGraphics.setDepth(100)
    this.scanlineGraphics.setAlpha(0.08)
    for (let y = 0; y < height; y += 4) {
      this.scanlineGraphics.fillStyle(0x000000, 1)
      this.scanlineGraphics.fillRect(0, y + 2, width, 2)
    }

    // Crosshair lines
    const crosshair = this.add.graphics()
    crosshair.lineStyle(1, HUD_GREEN, 0.3)
    crosshair.lineBetween(cx - 200, cy, cx + 200, cy) // horizontal
    crosshair.lineBetween(cx, cy - 150, cx, cy + 150) // vertical
    // Dashed circle
    crosshair.lineStyle(1, HUD_GREEN, 0.2)
    const segments = 60
    for (let i = 0; i < segments; i += 2) {
      const a1 = (i / segments) * Math.PI * 2
      const a2 = ((i + 1) / segments) * Math.PI * 2
      crosshair.lineBetween(
        cx + Math.cos(a1) * 140, cy + Math.sin(a1) * 140,
        cx + Math.cos(a2) * 140, cy + Math.sin(a2) * 140,
      )
    }

    // Reticle (drawn dynamically for animations)
    this.reticleGraphics = this.add.graphics()
    this.reticleGraphics.setDepth(10)

    // TARGET LOCK label
    this.targetLockLabel = this.add.text(cx, cy - 80, 'TARGET LOCK', {
      fontFamily: "'Share Tech Mono', monospace",
      fontSize: '11px',
      color: HUD_GREEN_DIM,
      letterSpacing: 4,
    }).setOrigin(0.5).setDepth(11)

    // Problem text (center of reticle)
    this.problemText = this.add.text(cx, cy, '', {
      fontFamily: "'Orbitron', sans-serif",
      fontSize: '48px',
      color: HUD_GREEN_CSS,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(12)

    // HUD elements — top bar
    this.engagementText = this.add.text(20, 20, '', {
      fontFamily: "'Share Tech Mono', monospace",
      fontSize: '14px',
      color: HUD_GREEN_CSS,
    }).setDepth(50)

    this.streakText = this.add.text(cx, 20, '', {
      fontFamily: "'Share Tech Mono', monospace",
      fontSize: '14px',
      color: HUD_GREEN_CSS,
    }).setOrigin(0.5, 0).setDepth(50)

    this.scoreText = this.add.text(width - 20, 20, '', {
      fontFamily: "'Orbitron', sans-serif",
      fontSize: '20px',
      color: HUD_GREEN_CSS,
      fontStyle: 'bold',
    }).setOrigin(1, 0).setDepth(50)

    // Score label
    this.add.text(width - 20, 8, 'SCORE', {
      fontFamily: "'Share Tech Mono', monospace",
      fontSize: '10px',
      color: HUD_GREEN_DIM,
    }).setOrigin(1, 0).setDepth(50)

    // Create answer button containers
    this.createAnswerButtons(cx, cy)

    // Load contacts from registry (set by React)
    this.contacts = this.registry.get('contacts') || []
    if (this.contacts.length > 0) {
      this.showContact(0)
    }
  }

  private createAnswerButtons(cx: number, cy: number) {
    this.answerButtons = []

    for (let i = 0; i < 4; i++) {
      const pos = this.buttonPositions[i]
      const bx = cx + pos.x
      const by = cy + pos.y

      const container = this.add.container(bx, by).setDepth(20)

      // Background
      const bg = this.add.graphics()
      bg.fillStyle(0x000000, 0.6)
      bg.fillRoundedRect(-42, -28, 84, 56, 4)
      bg.lineStyle(2, HUD_GREEN, 0.7)
      bg.strokeRoundedRect(-42, -28, 84, 56, 4)

      // Corner brackets
      const corners = this.add.graphics()
      corners.lineStyle(2, HUD_GREEN, 0.9)
      // top-left
      corners.lineBetween(-42, -20, -42, -28)
      corners.lineBetween(-42, -28, -34, -28)
      // top-right
      corners.lineBetween(42, -20, 42, -28)
      corners.lineBetween(42, -28, 34, -28)
      // bottom-left
      corners.lineBetween(-42, 20, -42, 28)
      corners.lineBetween(-42, 28, -34, 28)
      // bottom-right
      corners.lineBetween(42, 20, 42, 28)
      corners.lineBetween(42, 28, 34, 28)

      // Answer text
      const text = this.add.text(0, 0, '', {
        fontFamily: "'Orbitron', sans-serif",
        fontSize: '24px',
        color: HUD_GREEN_CSS,
        fontStyle: 'bold',
      }).setOrigin(0.5)

      container.add([bg, corners, text])

      // Make interactive
      const hitZone = this.add.zone(bx, by, 84, 56).setInteractive({ useHandCursor: true })
      hitZone.setDepth(25)
      hitZone.on('pointerover', () => {
        bg.clear()
        bg.fillStyle(HUD_GREEN, 0.2)
        bg.fillRoundedRect(-42, -28, 84, 56, 4)
        bg.lineStyle(2, HUD_GREEN, 1)
        bg.strokeRoundedRect(-42, -28, 84, 56, 4)
      })
      hitZone.on('pointerout', () => {
        bg.clear()
        bg.fillStyle(0x000000, 0.6)
        bg.fillRoundedRect(-42, -28, 84, 56, 4)
        bg.lineStyle(2, HUD_GREEN, 0.7)
        bg.strokeRoundedRect(-42, -28, 84, 56, 4)
      })
      hitZone.on('pointerdown', () => {
        sfx.click?.play()
        this.onAnswer(i)
      })

      this.answerButtons.push(container)
      // Store the hitZone reference for enabling/disabling
      container.setData('hitZone', hitZone)
      container.setData('bg', bg)
      container.setData('text', text)
    }
  }

  private showContact(index: number) {
    this.currentIndex = index
    this.attempts = 0
    this.contactStartTime = Date.now()

    const contact = this.contacts[index]
    if (!contact) return

    // Update problem text
    this.problemText.setText(contact.item.prompt)

    // Update answer buttons
    for (let i = 0; i < 4; i++) {
      const container = this.answerButtons[i]
      const text = container.getData('text') as Phaser.GameObjects.Text
      text.setText(contact.allChoices[i] ?? '')
      text.setColor(HUD_GREEN_CSS)
      container.setAlpha(1)
      container.setScale(1)
      // Re-enable
      const hitZone = container.getData('hitZone') as Phaser.GameObjects.Zone
      hitZone.setInteractive({ useHandCursor: true })
    }

    // Draw reticle (normal state)
    this.drawReticle('normal')

    // Update HUD
    const padded = String(index + 1).padStart(2, '0')
    const total = String(this.contacts.length).padStart(2, '0')
    this.engagementText.setText(`CONTACT ${padded}/${total}`)
    this.streakText.setText(this.streak > 0 ? `STREAK ×${this.streak}` : '')
    this.scoreText.setText(this.score.toLocaleString())

    // Entrance animation
    this.problemText.setAlpha(0).setScale(0.8)
    this.tweens.add({
      targets: this.problemText,
      alpha: 1,
      scale: 1,
      duration: 200,
      ease: 'Power2',
    })

    for (let i = 0; i < this.answerButtons.length; i++) {
      const btn = this.answerButtons[i]
      btn.setAlpha(0)
      this.tweens.add({
        targets: btn,
        alpha: 1,
        duration: 200,
        delay: 80 + i * 50,
        ease: 'Power2',
      })
    }

    // Emit stats event
    this.emitStats()
  }

  private onAnswer(buttonIndex: number) {
    const contact = this.contacts[this.currentIndex]
    if (!contact) return

    this.attempts++
    const chosenAnswer = contact.allChoices[buttonIndex]
    const isCorrect = chosenAnswer === contact.item.correctAnswer
    const responseTimeMs = Date.now() - this.contactStartTime

    if (isCorrect) {
      // Correct!
      this.streak++
      if (this.streak > this.bestStreak) this.bestStreak = this.streak

      // Score: base 100 + speed bonus + streak bonus
      const speedBonus = Math.max(0, Math.floor((5000 - responseTimeMs) / 50))
      const streakBonus = Math.min(this.streak, 10) * 20
      const contactScore = 100 + speedBonus + streakBonus
      this.score += contactScore

      // SFX
      if (this.streak > 0 && this.streak % 5 === 0) {
        sfx.streak?.play()
      } else {
        sfx.lock?.play()
      }

      // Quality rating (matches MATH_QUALITY_THRESHOLDS in mastery engine)
      let qualityRating = 5
      if (this.attempts > 1) qualityRating = 2
      else if (responseTimeMs > 5000) qualityRating = 3
      else if (responseTimeMs > 3000) qualityRating = 4

      const result: ContactResult = {
        itemId: contact.item.id,
        prompt: contact.item.prompt,
        correctAnswer: contact.item.correctAnswer,
        studentAnswer: chosenAnswer,
        isCorrect: true,
        responseTimeMs,
        qualityRating,
        attempts: this.attempts,
      }
      this.results.push(result)

      // Animate: reticle tighten + flash
      this.drawReticle('lock')
      this.correctAnimation(buttonIndex)

      // Emit answer event
      this.emitAnswer(result)

      // Advance after animation
      this.time.delayedCall(600, () => {
        if (this.currentIndex + 1 < this.contacts.length) {
          this.showContact(this.currentIndex + 1)
        } else {
          this.onSortieComplete()
        }
      })

    } else {
      // Incorrect
      this.streak = 0
      sfx.neg?.play()

      // Shake + flash red on chosen button
      this.incorrectAnimation(buttonIndex)

      // Don't record until they get it right — show correct, then retry
      // Disable wrong button
      const container = this.answerButtons[buttonIndex]
      const hitZone = container.getData('hitZone') as Phaser.GameObjects.Zone
      hitZone.disableInteractive()
      const text = container.getData('text') as Phaser.GameObjects.Text
      text.setColor('#FF333366')
      container.setAlpha(0.3)

      // Briefly pulse the correct answer button to guide the student
      const correctIdx = contact.correctIndex
      if (correctIdx >= 0 && correctIdx !== buttonIndex) {
        const correctContainer = this.answerButtons[correctIdx]
        const correctBg = correctContainer.getData('bg') as Phaser.GameObjects.Graphics

        correctBg.clear()
        correctBg.fillStyle(HUD_GREEN, 0.15)
        correctBg.fillRoundedRect(-42, -28, 84, 56, 4)
        correctBg.lineStyle(3, HUD_GREEN, 1)
        correctBg.strokeRoundedRect(-42, -28, 84, 56, 4)

        this.tweens.add({
          targets: correctContainer,
          scale: 1.08,
          duration: 150,
          yoyo: true,
          ease: 'Power2',
        })

        this.time.delayedCall(500, () => {
          correctBg.clear()
          correctBg.fillStyle(0x000000, 0.6)
          correctBg.fillRoundedRect(-42, -28, 84, 56, 4)
          correctBg.lineStyle(2, HUD_GREEN, 0.7)
          correctBg.strokeRoundedRect(-42, -28, 84, 56, 4)
        })
      }

      // Shake reticle
      this.drawReticle('miss')
      this.time.delayedCall(300, () => this.drawReticle('normal'))
    }
  }

  private correctAnimation(buttonIndex: number) {
    const container = this.answerButtons[buttonIndex]

    // Flash the correct button white then green
    const text = container.getData('text') as Phaser.GameObjects.Text
    text.setColor('#FFFFFF')
    this.time.delayedCall(150, () => text.setColor(HUD_GREEN_CSS))

    // Scale pulse
    this.tweens.add({
      targets: container,
      scale: 1.15,
      duration: 100,
      yoyo: true,
      ease: 'Power2',
    })

    // Fade out all buttons
    this.time.delayedCall(200, () => {
      for (const btn of this.answerButtons) {
        this.tweens.add({
          targets: btn,
          alpha: 0,
          duration: 300,
          ease: 'Power2',
        })
      }
    })
  }

  private incorrectAnimation(buttonIndex: number) {
    const container = this.answerButtons[buttonIndex]

    // Shake
    const startX = container.x
    this.tweens.add({
      targets: container,
      x: startX + 8,
      duration: 40,
      yoyo: true,
      repeat: 3,
      ease: 'Linear',
      onComplete: () => {
        container.x = startX
      },
    })
  }

  private drawReticle(state: 'normal' | 'lock' | 'miss') {
    const g = this.reticleGraphics
    const cx = this.scale.width / 2
    const cy = this.scale.height / 2
    g.clear()

    if (state === 'normal') {
      // Main box
      g.lineStyle(2, HUD_GREEN, 0.8)
      g.strokeRect(cx - 100, cy - 50, 200, 100)

      // Corner brackets
      g.lineStyle(3, HUD_GREEN, 1)
      const s = 15
      // top-left
      g.lineBetween(cx - 100, cy - 50 + s, cx - 100, cy - 50)
      g.lineBetween(cx - 100, cy - 50, cx - 100 + s, cy - 50)
      // top-right
      g.lineBetween(cx + 100 - s, cy - 50, cx + 100, cy - 50)
      g.lineBetween(cx + 100, cy - 50, cx + 100, cy - 50 + s)
      // bottom-left
      g.lineBetween(cx - 100, cy + 50 - s, cx - 100, cy + 50)
      g.lineBetween(cx - 100, cy + 50, cx - 100 + s, cy + 50)
      // bottom-right
      g.lineBetween(cx + 100 - s, cy + 50, cx + 100, cy + 50)
      g.lineBetween(cx + 100, cy + 50, cx + 100, cy + 50 - s)

    } else if (state === 'lock') {
      // Tightened reticle — smaller, brighter
      g.lineStyle(3, HUD_GREEN, 1)
      g.strokeRect(cx - 70, cy - 35, 140, 70)

      // Bright corners
      const s = 12
      g.lineStyle(4, 0xffffff, 1)
      g.lineBetween(cx - 70, cy - 35 + s, cx - 70, cy - 35)
      g.lineBetween(cx - 70, cy - 35, cx - 70 + s, cy - 35)
      g.lineBetween(cx + 70 - s, cy - 35, cx + 70, cy - 35)
      g.lineBetween(cx + 70, cy - 35, cx + 70, cy - 35 + s)
      g.lineBetween(cx - 70, cy + 35 - s, cx - 70, cy + 35)
      g.lineBetween(cx - 70, cy + 35, cx - 70 + s, cy + 35)
      g.lineBetween(cx + 70 - s, cy + 35, cx + 70, cy + 35)
      g.lineBetween(cx + 70, cy + 35, cx + 70, cy + 35 - s)

      this.targetLockLabel.setText('LOCK CONFIRMED')
      this.targetLockLabel.setColor('#FFFFFF')
      this.time.delayedCall(400, () => {
        this.targetLockLabel.setText('TARGET LOCK')
        this.targetLockLabel.setColor(HUD_GREEN_DIM)
      })

    } else if (state === 'miss') {
      // Widened + red
      g.lineStyle(2, 0xff3333, 0.8)
      g.strokeRect(cx - 120, cy - 60, 240, 120)

      g.lineStyle(3, 0xff3333, 1)
      const s = 15
      g.lineBetween(cx - 120, cy - 60 + s, cx - 120, cy - 60)
      g.lineBetween(cx - 120, cy - 60, cx - 120 + s, cy - 60)
      g.lineBetween(cx + 120 - s, cy - 60, cx + 120, cy - 60)
      g.lineBetween(cx + 120, cy - 60, cx + 120, cy - 60 + s)
      g.lineBetween(cx - 120, cy + 60 - s, cx - 120, cy + 60)
      g.lineBetween(cx - 120, cy + 60, cx - 120 + s, cy + 60)
      g.lineBetween(cx + 120 - s, cy + 60, cx + 120, cy + 60)
      g.lineBetween(cx + 120, cy + 60, cx + 120, cy + 60 - s)
    }
  }

  private onSortieComplete() {
    sfx.mission?.play()

    const totalContacts = this.results.length
    const correctCount = this.results.filter(r => r.isCorrect).length
    const accuracy = totalContacts > 0 ? correctCount / totalContacts : 0
    const avgTimeMs = totalContacts > 0
      ? this.results.reduce((s, r) => s + r.responseTimeMs, 0) / totalContacts
      : 0

    const event: FactStrikeCompleteEvent = {
      results: this.results,
      totalScore: this.score,
      bestStreak: this.bestStreak,
      accuracy,
      avgTimeMs,
    }

    window.dispatchEvent(new CustomEvent('fact-strike:complete', { detail: event }))
  }

  private emitAnswer(result: ContactResult) {
    const event: FactStrikeAnswerEvent = {
      itemId: result.itemId,
      studentAnswer: result.studentAnswer,
      isCorrect: result.isCorrect,
      responseTimeMs: result.responseTimeMs,
      streak: this.streak,
      score: this.score,
    }
    window.dispatchEvent(new CustomEvent('fact-strike:answer', { detail: event }))
  }

  private emitStats() {
    window.dispatchEvent(new CustomEvent('fact-strike:stats', {
      detail: {
        engagement: this.currentIndex,
        total: this.contacts.length,
        streak: this.streak,
        score: this.score,
      },
    }))
  }
}

// ─── React Component ──────────────────────────────────────────────

interface EngagementHUDProps {
  contacts: Contact[]
  onAnswer?: (event: FactStrikeAnswerEvent) => void
  onComplete?: (event: FactStrikeCompleteEvent) => void
}

export default function EngagementHUD({ contacts, onAnswer, onComplete }: EngagementHUDProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const gameRef = useRef<Phaser.Game | null>(null)

  // Stable callback refs
  const onAnswerRef = useRef(onAnswer)
  onAnswerRef.current = onAnswer
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const handleAnswer = useCallback((e: Event) => {
    const detail = (e as CustomEvent<FactStrikeAnswerEvent>).detail
    onAnswerRef.current?.(detail)
  }, [])

  const handleComplete = useCallback((e: Event) => {
    const detail = (e as CustomEvent<FactStrikeCompleteEvent>).detail
    onCompleteRef.current?.(detail)
  }, [])

  useEffect(() => {
    if (!containerRef.current || gameRef.current) return

    loadSFX()

    const game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: containerRef.current,
      width: 800,
      height: 600,
      backgroundColor: HUD_BG,
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      scene: FactStrikeScene,
      audio: { noAudio: true }, // Using Howler instead
    })

    // Pass contacts to scene via registry
    game.registry.set('contacts', contacts)

    gameRef.current = game

    // Listen for events
    window.addEventListener('fact-strike:answer', handleAnswer)
    window.addEventListener('fact-strike:complete', handleComplete)

    return () => {
      window.removeEventListener('fact-strike:answer', handleAnswer)
      window.removeEventListener('fact-strike:complete', handleComplete)
      game.destroy(true)
      gameRef.current = null
    }
  }, [contacts, handleAnswer, handleComplete])

  return (
    <div className="w-full h-full bg-[#020617] flex items-center justify-center">
      <div ref={containerRef} className="w-full h-full max-w-[1000px] max-h-[750px]" />
    </div>
  )
}
